<script setup lang="ts">
/**
 * Module: web/src/views/PinLoginView.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.0
 * Date: 2026-07-08
 *
 * Quick-PIN partial login (doc 04 sect 6.5). person_ref is a short pre-shared
 * code / name (NOT an email) -- plain text input -- plus a 4-digit PIN pad
 * with large buttons. Auto-submits when the 4th digit lands and a person ref
 * is present. Produces a LIMITED session that lands on /quick.
 */
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useLockout } from '@/composables/useLockout';
import { ApiError } from '@/composables/useApi';
import AppIcon from '@/components/AppIcon.vue';
import PinPad from '@/components/PinPad.vue';

const auth = useAuthStore();
const router = useRouter();
const { lockedOut, remainingLabel, startLockout } = useLockout();

const personRef = ref('');
const pin = ref('');
const submitting = ref(false);
const errorMessage = ref('');

async function submit() {
  if (submitting.value || lockedOut.value) return;
  errorMessage.value = '';
  if (!personRef.value.trim()) {
    errorMessage.value = 'Enter your name or access code first.';
    pin.value = '';
    return;
  }
  if (pin.value.length !== 4) {
    errorMessage.value = 'Enter your 4-digit PIN.';
    return;
  }
  submitting.value = true;
  try {
    await auth.pinLogin(personRef.value.trim(), pin.value);
    void router.push('/quick');
  } catch (e) {
    pin.value = '';
    if (e instanceof ApiError && e.status === 423) {
      startLockout(e.field('unlock_at'));
      errorMessage.value = '';
    } else if (e instanceof ApiError && e.status === 401) {
      errorMessage.value = 'Invalid PIN.';
    } else {
      errorMessage.value = 'Quick access failed. Check your connection and try again.';
    }
  } finally {
    submitting.value = false;
  }
}

function onPinComplete() {
  // Auto-submit on the 4th digit when the person ref is already filled.
  if (personRef.value.trim()) void submit();
}
</script>

<template>
  <div class="min-h-screen flex flex-col items-center justify-center px-4 py-8">
    <div class="w-full max-w-sm">
      <div class="text-center mb-6">
        <div class="mx-auto w-14 h-14 rounded-2xl bg-accent/15 border border-accent/30 flex items-center justify-center mb-4">
          <AppIcon name="key" class="w-7 h-7 text-accent" />
        </div>
        <h1 class="text-2xl font-bold text-fg">Quick PIN access</h1>
        <p class="text-sm text-fg-muted mt-1">
          Limited access to low-risk features only. For full access,
          <RouterLink to="/login" class="text-accent hover:underline">sign in</RouterLink>.
        </p>
      </div>

      <div v-if="lockedOut" class="alert-error flex items-start gap-2.5 mb-4" role="alert">
        <AppIcon name="lock" class="w-5 h-5 flex-shrink-0 mt-0.5" />
        <span>
          Too many attempts.
          <span class="font-semibold whitespace-nowrap">Try again in {{ remainingLabel }}.</span>
        </span>
      </div>
      <div v-else-if="errorMessage" class="alert-error mb-4" role="alert">{{ errorMessage }}</div>

      <div class="mb-5">
        <label class="field-label" for="pin-person">Name or access code</label>
        <input
          id="pin-person"
          v-model="personRef"
          class="field-input"
          type="text"
          autocomplete="off"
          autocapitalize="none"
          spellcheck="false"
          placeholder="e.g. ernest"
          :disabled="submitting || lockedOut"
        />
      </div>

      <PinPad v-model="pin" :disabled="submitting || lockedOut" @complete="onPinComplete" />

      <button
        type="button"
        class="btn-primary w-full mt-5"
        :disabled="submitting || lockedOut || pin.length !== 4"
        @click="submit"
      >
        {{ submitting ? 'Checking…' : 'Enter' }}
      </button>

      <div class="text-center mt-5">
        <RouterLink to="/login" class="inline-block py-2.5 px-3 text-sm text-accent hover:underline">
          Back to sign in
        </RouterLink>
      </div>
    </div>
  </div>
</template>
